import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it as itLocale, enUS } from 'date-fns/locale';
import { Share2, Star, ExternalLink, User, Lightbulb, Loader2, Scan, Contrast, ZoomIn, ZoomOut } from 'lucide-react';
import { GradeBadge } from '../climb-log/GradeBadge';
import { HoldsOverlay } from './HoldsOverlay';
import { RouteLineOverlay } from './RouteLineOverlay';
import { HoldsModeToggleButton } from './HoldsModeToggleButton';
import { RouteLineModeToggleButton } from './RouteLineModeToggleButton';
import { useHoldsDisplayMode } from './holds-display-mode';
import { climbPhotoPaint, useClimbPhotoEffectControl } from './climb-photo-effect';
import { routeLineDisplay, useRouteLineDisplayMode } from './route-line-display-mode';
import { hasCorridor, isDrawableLine } from './route-line';
import {
  cropImageStyle,
  cropOutruns,
  cropViewBox,
  fitCrop,
  holdsBounds,
  padRect,
} from './hold-focus';
import BoardGridDisplay from './board-grid/BoardGridDisplay';
import { climbWall } from './board-grid/climb-wall';
import { circuitHighlightColor } from './board-grid/roles';
import { useZoomPan } from './board-grid/useZoomPan';
import { useBoxSize } from './use-box-size';
import { climbTitle } from '../../utils/climb-title';
import type { Climb } from '../../types/climb';
import type { Board } from '../../types/device';
import type { CSSProperties, ReactNode } from 'react';

/**
 * A little room around the circles, as a fraction of the box they span: a ring
 * flush with the frame's edge reads as cut off even when every pixel of it is
 * on screen.
 */
const COVER_HOLDS_PADDING = 0.04;

/**
 * How tall the frame grows when the picture is opened out: as much of the screen
 * as a photograph can have and still leave the page it is the header of in
 * sight, under a navbar it is pinned below. The width follows from it, so a
 * portrait wall is drawn narrower rather than cropped — see `fitHeight`.
 */
const EXPANDED_HEIGHT = '78dvh';

/**
 * What the photograph behind the photograph is drawn with, where the crop runs
 * past the picture's edges: blurred far enough not to read as a second picture
 * and set back far enough to read as the room around the first one. The
 * Tailwind pair it replaces (`blur-2xl brightness-[0.55]`), as a filter string,
 * because the reader's own effect has to be appended to it.
 */
const BACKDROP_FILTER = 'blur(40px) brightness(0.55)';

interface ClimbCoverImageProps {
  climb: Climb;
  /** The board a board climb is viewed through — its photo & calibration win. */
  board?: Board | null;
  /**
   * Whether a press on the picture — or on the name written across it — opens the
   * frame out to the whole photograph, and a second press puts it back.
   *
   * The climb's own page, where the cover is a strip of a wall cropped to the
   * holds (see `fitCrop`) and what the reader wants next is the rest of the
   * photograph: the wall around the climb, how high the top really is. They get
   * it in the page they are already on rather than in a dialog over it.
   *
   * Only where there is a photograph to open out: a board is drawn whole
   * already, and a climb with no picture has nothing to show.
   */
  expandable?: boolean;
  onShare?: () => void;
  /**
   * Offers the reader a button, top right, that cycles how the photograph is
   * shown (see `climb-photo-effect.ts`). Only where there is something to cycle:
   * a climb made off a scanned wall, with its photograph on screen.
   */
  photoEffectToggle?: boolean;
  /** When provided, shows an Instagram share button (navigate to /share). Always visible. */
  onInstagramShare?: () => void;
  /** When provided, shows a navigate-to-climb button instead of the share button. */
  onNavigate?: () => void;
  /** Called when user clicks the rating overlay. Only attached if provided. */
  onRate?: () => void;
  userAuthenticated?: boolean;
  className?: string;
  /**
   * How tall a board photo may be, as a CSS length. It is drawn at its own
   * aspect ratio — the holds are placed in percentages of the picture's box —
   * so it is narrowed to fit rather than cropped, and stays full width as long
   * as that keeps it under this. A normal cover crops itself to `className`.
   */
  maxBoardHeight?: string;
  /** Optional admin actions row rendered below the info row. */
  adminActions?: ReactNode;
  /**
   * What to put where the picture would be, on a climb that has none: the offer
   * to take one (see ClimbPhotoPrompt). Passed only by the climb's own page —
   * everywhere else a missing photo is the letter placeholder, because a row in a
   * feed is not a place to start a camera. The frame then keeps its overlays
   * (the rating, the chips, sharing) but drops what belongs to a photograph: the
   * gradient and the white title written across it.
   */
  photoPrompt?: ReactNode;
  /**
   * What fills the frame instead of the picture: a route's 3D model. The frame keeps
   * its size, the title and the rating, and drops what belongs to a photograph.
   */
  frameContent?: ReactNode;
  /** Top right over the frame, whichever it is showing: the switch between the two. */
  frameControls?: ReactNode;
  /**
   * Rendered between the photo and the info row — the circuit player's bar,
   * whose play button is meant to straddle the two.
   */
  imageFooter?: ReactNode;
  /**
   * Index of the hold a circuit run is on, dimming the board photo down to it.
   * Board climbs only.
   */
  currentHold?: number | null;
  /** When provided, replaces the default "created date" slot in the info row. */
  infoRowTrailing?: ReactNode;
  /** When set, renders a small chip with this text in the bottom-right corner of the image. */
  holdColorOverlay?: string;
  /**
   * When provided, overlays a lightbulb button at the centre of the board photo
   * that lights the climb's holds on the wall. Only passed for a board climb
   * whose board can be lit — by its own device, or by this phone.
   */
  onLight?: () => void;
  /**
   * What the lightbulb says it does, when "light the holds" is not the whole of
   * it — the first press on a board this phone lights itself has a Bluetooth
   * chooser to get through first.
   */
  lightLabel?: string;
  /** Keeps the lightbulb button busy while the command is in flight. */
  lighting?: boolean;
}

export function ClimbCoverImage({
  climb,
  board,
  expandable = false,
  onShare,
  photoEffectToggle = false,
  onInstagramShare,
  onNavigate,
  onRate,
  userAuthenticated = false,
  className = 'w-full h-64',
  maxBoardHeight,
  adminActions,
  photoPrompt,
  frameContent,
  frameControls,
  infoRowTrailing,
  holdColorOverlay,
  onLight,
  lightLabel,
  lighting = false,
  imageFooter,
  currentHold = null,
}: ClimbCoverImageProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'it' ? itLocale : enUS;
  // The size the picture on screen reported when it loaded, and **which picture
  // that was**: swiping to the next climb swaps the photograph under this frame
  // without remounting it, and a crop worked out from the shape of the last one
  // would put the circles somewhere they are not.
  const [measured, setMeasured] = useState<{ src: string; width: number; height: number } | null>(null);
  // A photo of a wall is worth looking closely at — which hold wears which number
  // on a circuit, whether the top is the one you thought. A board photo has had
  // this all along; this is the same pinch, wheel and pair of buttons.
  const zoom = useZoomPan();
  // Persisted (and shared across all climbs) holds display mode.
  const { mode: holdsMode, cycle: cycleHoldsMode } = useHoldsDisplayMode();
  // And how this reader has asked for a wall with a climb on it to be shown:
  // the wall pushed back and the climb's own holds given back, or the bare
  // photograph. A preference of theirs — but one that only a climb made off a
  // scanned wall can wear, so the climb is handed over to be asked. See
  // `climb-photo-effect.ts`.
  const { effect: photoEffect, applies: photoEffectApplies, cycle: cyclePhotoEffect } =
    useClimbPhotoEffectControl(climb);
  const paint = climbPhotoPaint(photoEffect);
  const { mode: lineMode, cycle: cycleLineMode } = useRouteLineDisplayMode();
  const hasHolds = !!climb.holds && climb.holds.length > 0;
  const hasLine = isDrawableLine(climb.line);
  const lineDisplay = routeLineDisplay(lineMode, hasCorridor(climb.line));
  // On a climb drawn as a line the lightbulb belongs to the line, so any holds
  // the setter also circled ride along with it rather than being stranded on a
  // toggle that is no longer on screen.
  const coverHoldsMode = hasLine ? (lineDisplay.line ? 'all' : 'none') : holdsMode;

  const title = climbTitle(climb);
  const imageSrc =
    climb.image_medium_url ||
    climb.image_url ||
    `https://placehold.co/800x256.png?text=${encodeURIComponent(title.charAt(0))}`;
  const hasRealImage = climb.has_image && !!(climb.image_medium_url || climb.image_url);
  const showsPicture = !frameContent;
  const showsPhotoEffectToggle = photoEffectToggle && photoEffectApplies && hasRealImage && showsPicture;
  // Where the picture would be, the way to supply one. It takes the frame's own
  // height with it: the panel is as tall as its two buttons, not as tall as the
  // photograph that is not there.
  const showsPrompt = !hasRealImage && !!photoPrompt && showsPicture;

  // Board climbs render the board photo with their grid holds, not a per-climb
  // image — the board's own photo where it is viewed through one (see climbWall).
  const isBoardClimb = climb.type === 'BoardClimb';
  const wall = isBoardClimb ? climbWall(climb, board) : null;
  const boardImageUrl = wall?.imageUrl ?? null;

  // The picture this frame is showing, and its size — only while the two still
  // belong together. A board's photo is the wall's rather than the problem's,
  // so it survives a swipe from one problem to the next and is not reloaded;
  // keyed by its own URL, it keeps the size it already reported.
  const pictureSrc = (isBoardClimb && boardImageUrl) || imageSrc;
  const imageDims = measured && measured.src === pictureSrc ? measured : null;
  // And its shape, which is what bounds a picture drawn whole — a board, and a
  // photograph that has been opened out (see `fitHeight`).
  const pictureAspect = imageDims ? imageDims.width / imageDims.height : null;

  // **Opened out: the whole photograph instead of the climb on it.** The crop is
  // dropped and the frame takes the picture's own shape, as tall as a wall can
  // be read at — a press on the picture asks for this, and the next one gives
  // the cover back. A board is drawn whole to begin with, so it has nothing to
  // open; nor has a climb whose photograph is missing.
  const [openedOut, setExpanded] = useState(false);
  const expanded = openedOut && showsPicture;
  const canExpand = expandable && hasRealImage && !(isBoardClimb && boardImageUrl) && showsPicture;
  const toggleExpanded = canExpand ? () => setExpanded((open) => !open) : undefined;

  // **How the picture is framed: so that every circle on it is on screen.** A
  // hero of a fixed height crops the middle out of a photograph, and on a
  // portrait wall what that cuts off is the top hold and the start — the two
  // the reader is looking for. So the crop is the holds' box grown to the
  // frame's shape, opened out past the edges of the photo where it has to be
  // (see `fitCrop`); the frame is measured because its shape is the screen's,
  // a share of the viewport's height by whatever the column happens to be wide.
  //
  // A climb drawn as a line is left exactly as it was: a line is not a set of
  // marks with a box round them but a stroke the height of the wall, and the
  // overlay that draws it takes no crop.
  const frameBox = useBoxSize(zoom.frame);
  const frameAspect = frameBox.height > 0 ? frameBox.width / frameBox.height : 0;
  const holdsBox = useMemo(
    () => (hasRealImage && !hasLine ? holdsBounds(climb.holds) : null),
    [hasRealImage, hasLine, climb.holds]
  );
  const crop = useMemo(
    () =>
      holdsBox && imageDims && !expanded
        ? fitCrop(
            padRect(holdsBox, COVER_HOLDS_PADDING),
            imageDims.width,
            imageDims.height,
            frameAspect
          )
        : null,
    [holdsBox, imageDims, frameAspect, expanded]
  );
  // Where the crop reaches past the photo, the frame has room left over — and
  // what goes in it is the photograph itself, blurred back. A band of flat
  // colour down either side of a hero reads as something having gone wrong.
  const spillsOver = cropOutruns(crop);

  // The next climb arrives in the frame the last one was being read in, which
  // is still zoomed into the corner of a wall that is no longer on screen, and
  // perhaps opened out to the height of a photograph that has gone with it.
  // Nobody means to carry either from one climb to the next.
  const { reset: resetZoom } = zoom;
  useEffect(() => {
    resetZoom();
    setExpanded(false);
  }, [climb.id, resetZoom]);

  // **How big the frame is.** A picture drawn whole — a board, and a photograph
  // opened out — is bounded by its height and narrowed to keep its shape (see
  // `fitHeight`). Everything else is the hero of the height the caller asked
  // for, except where the photograph is missing and there is an offer in its
  // place: that panel is as tall as its own two buttons.
  const drawnWhole = (isBoardClimb && !!boardImageUrl) || expanded;
  const coverClassName = drawnWhole
    ? 'w-full mx-auto'
    : showsPrompt
      ? 'w-full border-b'
      : className;
  const coverStyle = isBoardClimb && boardImageUrl
    ? (maxBoardHeight ? fitHeight(maxBoardHeight, pictureAspect) : undefined)
    : expanded
      ? fitHeight(EXPANDED_HEIGHT, pictureAspect)
      : undefined;

  return (
    <>
    {/* Board climbs size to their photo's natural aspect so the grid overlay
        aligns; other climbs keep the fixed hero height until they are opened
        out, when they take their photograph's shape too. */}
    <div
      className={`relative overflow-hidden ${coverClassName}`}
      style={coverStyle}
      data-testid="climb-cover-image"
      data-expanded={expanded ? '' : undefined}
    >
      {frameContent ? (
        frameContent
      ) : showsPrompt ? (
        <div className="pt-3">
          {/* The title, in plain letters. There is nothing to write it across, so
              it is a heading like any other rather than white with a shadow — and
              only where the climb was given a name at all, which most gym
              boulders never are. */}
          {climb.name && <h1 className="truncate px-3 text-xl font-bold">{climb.name}</h1>}
          {photoPrompt}
        </div>
      ) : wall && boardImageUrl ? (
        <BoardGridDisplay
          imageUrl={boardImageUrl}
          rows={wall.rows}
          columns={wall.columns}
          calibration={wall.calibration}
          holds={wall.holds}
          boardHolds={wall.boardHolds}
          extraHolds={wall.extraHolds}
          shapes={wall.shapes}
          scanned={wall.scanned}
          holdRadii={wall.holdRadii}
          circuit={isBoardClimb && !!climb.circuit}
          currentHold={currentHold}
          highlightColor={circuitHighlightColor(board)}
          onNaturalSize={(size) => setMeasured({ src: pictureSrc, ...size })}
          // Reading a problem off the wall means looking closely at which holds
          // are in it — the same reason the setting grid is pinchable.
          zoomable
        />
      ) : (
        <>
          {/* The frame the picture is pinched about, and the layer that moves
              inside it: the photo and everything drawn on it share one transform,
              so a circle can never come away from the hold it is on. Reading a
              circuit means looking closely at which hold wears which number —
              the same reason a board photo is pinchable. */}
          <div
            {...zoom.frameProps}
            // Zoomed in, a sideways drag is a pan across the photo; the climb
            // profile's swipe to the next climb has to let this one go.
            {...(zoom.zoomed ? { 'data-swipe-ignore': true } : {})}
            // Opening the picture out is a press anywhere in the frame, not
            // only on the photograph: where the picture is narrower than the
            // frame, what the finger lands on either side of it is the room
            // around it, and a press there plainly means the same thing. A
            // press that was a drag never gets this far — see onClickCapture.
            onClick={toggleExpanded}
            title={canExpand ? t(expanded ? 'climbs.cover.collapse' : 'climbs.cover.expand') : undefined}
            className={`group relative h-full w-full select-none overflow-hidden ${canExpand ? 'cursor-pointer' : ''}`}
          >
            <div {...zoom.layerProps} className="relative h-full w-full">
              {/* The picture again, filling the frame behind the framed one. It
                  is drawn blurred and set back so it reads as the room around
                  the photograph rather than as a second photograph. */}
              {spillsOver && (
                <img
                  src={imageSrc}
                  alt=""
                  aria-hidden
                  className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover"
                  // Its own blur and its own darkness, plus whatever the reader
                  // has asked of the wall — in one filter, because an inline one
                  // would otherwise replace the Tailwind pair wholesale.
                  style={{ filter: [BACKDROP_FILTER, paint.wall].filter(Boolean).join(' ') }}
                  draggable={false}
                />
              )}
              <img
                src={imageSrc}
                alt={title}
                // Opened out, the photograph is what gives the frame its height:
                // drawn at its own shape across the full width, with `block` so
                // it is the whole of that height rather than a line of text with
                // a picture in it. The overlays follow — they are the box the
                // photo has made.
                className={`${crop ? 'absolute max-w-none' : expanded ? 'block h-auto w-full' : 'w-full h-full'} object-cover transition-opacity ${canExpand ? 'group-hover:opacity-90' : ''}`}
                // Blow the photo up until the crop alone fills the frame and
                // slide it into place — or, where the crop is bigger than the
                // photo, draw it smaller than the frame and centre it. And
                // push the wall back the way the reader asked.
                style={{ ...cropImageStyle(crop), filter: paint.wall }}
                draggable={false}
                onLoad={(e) =>
                  setMeasured({
                    src: pictureSrc,
                    width: e.currentTarget.naturalWidth,
                    height: e.currentTarget.naturalHeight,
                  })
                }
              />

              {/* Hold circles overlay (only over a real image) */}
              {hasRealImage && (
                <HoldsOverlay
                  holds={climb.holds}
                  naturalWidth={imageDims?.width ?? null}
                  naturalHeight={imageDims?.height ?? null}
                  // The same piece of the photo the picture is showing, so a
                  // circle lands on the hold it was drawn on however the frame
                  // is cropped.
                  viewBox={cropViewBox(crop, imageDims)}
                  objectFit={expanded ? 'contain' : 'cover'}
                  mode={coverHoldsMode}
                  // A circuit is the order its holds are climbed in, and a
                  // photograph can only say that with the numbers on the circles.
                  circuit={!!climb.circuit}
                  currentHold={currentHold}
                  // The wall pushed back with this climb's holds given their
                  // colour back — which stays on with the rings off, and is
                  // then the only thing saying which climb this is.
                  effect={photoEffect}
                  imageSrc={imageSrc}
                />
              )}

              {/* Route line overlay — a route is drawn rather than circled */}
              {hasRealImage && hasLine && (lineDisplay.line || lineDisplay.focus) && (
                <RouteLineOverlay
                  line={climb.line}
                  naturalWidth={imageDims?.width ?? null}
                  naturalHeight={imageDims?.height ?? null}
                  objectFit={expanded ? 'contain' : 'cover'}
                  showLine={lineDisplay.line}
                  focusOutside={lineDisplay.focus}
                />
              )}
            </div>
          </div>

          {/* Down the left edge, as on a board photo: the free side, clear of the
              rating below and the chips opposite. Out and reset stay put but dead
              until there is something to undo, so the stack does not jump about. */}
          {hasRealImage && (
            <div className="absolute left-2 top-1/2 flex -translate-y-1/2 flex-col gap-1">
              <ZoomControl
                label={t('climbs.board.zoom.in')}
                onClick={zoom.zoomIn}
                disabled={!zoom.canZoomIn}
              >
                <ZoomIn className="size-4" aria-hidden />
              </ZoomControl>
              <ZoomControl
                label={t('climbs.board.zoom.out')}
                onClick={zoom.zoomOut}
                disabled={!zoom.zoomed}
              >
                <ZoomOut className="size-4" aria-hidden />
              </ZoomControl>
              <ZoomControl
                label={t('climbs.board.zoom.reset')}
                onClick={zoom.reset}
                disabled={!zoom.zoomed}
              >
                <Scan className="size-4" aria-hidden />
              </ZoomControl>
            </div>
          )}
        </>
      )}

      {/* Light-the-LEDs button — bottom-centre of the board photo, clear of the
          rating overlay on the left and the chips on the right. Only board
          climbs on a wall that can actually be lit get one. */}
      {isBoardClimb && onLight && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onLight();
          }}
          disabled={lighting}
          title={lightLabel ?? t('climbs.board.light')}
          aria-label={lightLabel ?? t('climbs.board.light')}
          className="absolute bottom-2 left-1/2 -translate-x-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors disabled:opacity-60"
          data-testid="cover-light-button"
        >
          {lighting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Lightbulb className="h-4 w-4" />
          )}
        </button>
      )}

      {/* Top gradient, and the name written in white across it. Both are there to
          be read *against a photograph*; over the panel that offers to take one
          they would be a dark band and an unreadable title. */}
      {!showsPrompt && (
        <>
          <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />

          {/* Climb name overlay — written across the photograph, so it opens the
              photograph out like the rest of it, and is the keyboard's way in. */}
          <button
            onClick={toggleExpanded}
            className={`absolute top-0 left-0 ${frameControls ? 'right-24' : (onShare && onInstagramShare) ? 'right-20' : (onShare || onNavigate || onInstagramShare || showsPhotoEffectToggle) ? 'right-10' : 'right-0'} px-3 py-2 text-left ${!canExpand ? 'cursor-default' : ''}`}
          >
            <h1
              className="text-white font-bold text-xl truncate"
              style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.7)' }}
            >
              {climb.name}
            </h1>
          </button>
        </>
      )}

      {frameControls && <div className="absolute right-2 top-2">{frameControls}</div>}

      {/* Share button */}
      {onShare && (
        <button
          onClick={onShare}
          title={t('climbProfile.share')}
          className={`absolute top-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors ${onInstagramShare ? 'right-10' : 'right-2'}`}
          data-testid="cover-share-button"
        >
          <Share2 className="h-4 w-4" />
        </button>
      )}

      {/* Photo effect button — cycles how the wall is shown behind the climb */}
      {showsPhotoEffectToggle && (
        <button
          type="button"
          onClick={cyclePhotoEffect}
          title={t(`climbProfile.photoEffect.${photoEffect}`)}
          aria-label={t(`climbProfile.photoEffect.${photoEffect}`)}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          data-testid="cover-photo-effect-toggle"
        >
          <Contrast className={`h-4 w-4 ${photoEffect === 'none' ? 'opacity-40' : ''}`} />
        </button>
      )}

      {/* Instagram share button — visible to everyone */}
      {onInstagramShare && (
        <button
          onClick={onInstagramShare}
          title={t('shareClimb.title')}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          data-testid="cover-instagram-share-button"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
            <circle cx="12" cy="12" r="4" />
            <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
          </svg>
        </button>
      )}

      {/* Navigate-to-climb button (shown in lightbox context instead of share) */}
      {onNavigate && !onShare && (
        <button
          onClick={onNavigate}
          title={t('climbs.openClimb')}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          data-testid="cover-navigate-button"
        >
          <ExternalLink className="h-4 w-4" />
        </button>
      )}

      {/* Average rating overlay — bottom-left */}
      <button
        onClick={() => userAuthenticated && onRate?.()}
        className={`absolute bottom-2 left-2 flex items-center gap-1 bg-black/50 rounded-md px-2 py-1 ${userAuthenticated && onRate ? 'cursor-pointer hover:bg-black/70' : 'cursor-default'} transition-colors`}
        data-testid="cover-rating"
      >
        {[1, 2, 3, 4, 5].map((i) => {
          const rating = climb.average_rating ?? 0;
          const filled = rating >= i;
          const half = !filled && rating >= i - 0.5;
          return (
            <span key={i} className="relative inline-block">
              <Star className="h-3 w-3 text-gray-400" strokeWidth={1.5} />
              {(filled || half) && (
                <span
                  className="absolute inset-0 overflow-hidden"
                  style={{ width: half ? '50%' : '100%' }}
                >
                  <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" strokeWidth={1.5} />
                </span>
              )}
            </span>
          );
        })}
        <span className="text-xs text-white font-medium">
          {climb.average_rating?.toFixed(1) ?? '0.0'}
        </span>
      </button>

      {/* Holds display toggle — bottom-center. Cycles start&top → all → none. */}
      {!showsPicture ? null : hasRealImage && hasLine ? (
        <RouteLineModeToggleButton mode={lineMode} onToggle={cycleLineMode} />
      ) : hasRealImage && hasHolds ? (
        <HoldsModeToggleButton mode={holdsMode} onToggle={cycleHoldsMode} />
      ) : null}

      {/* Bottom-right overlays: hold color + top climb styles, stacked */}
      {(holdColorOverlay || (climb.top_user_climb_style && climb.top_user_climb_style.length > 0)) && (
        <div className="absolute bottom-2 right-2 flex flex-col items-end gap-1">
          {climb.top_user_climb_style && climb.top_user_climb_style.length > 0 && (
            <div className="flex gap-1">
              {climb.top_user_climb_style.map((style) => (
                <span
                  key={style}
                  className="text-xs text-white bg-black/50 rounded-md px-2 py-1"
                >
                  {t(`climbLog.${style}`)}
                </span>
              ))}
            </div>
          )}
          {holdColorOverlay && (
            <span
              className="text-xs text-white bg-black/60 rounded-md px-2 py-1 font-medium"
              data-testid="cover-hold-color"
            >
              {holdColorOverlay}
            </span>
          )}
        </div>
      )}
    </div>

    {/* Between the photo and the info row — the circuit player's bar, which
        overlaps both. */}
    {imageFooter}

    {/* Info row: difficulty · setter · date */}
    <div className="flex items-center justify-between px-3 py-2 border-b text-sm" data-testid="climb-info-row">
      <GradeBadge
        difficulty={climb.difficulty}
        averageGradeOpinion={climb.average_grade_opinion}
      />

      {/* A setter with an account links to it; a guest is a name and nothing
          more, because there is no profile to send anyone to. */}
      {climb.route_setter ? (
        (() => {
          const setter = climb.route_setter;
          const face = setter.avatar_small_url ? (
            <img
              src={setter.avatar_small_url}
              alt=""
              className="h-5 w-5 rounded-full object-cover"
            />
          ) : (
            <User className="h-3 w-3" />
          );
          const body = (
            <>
              {face}
              <span className="text-xs">{setter.name}</span>
            </>
          );

          return setter.slug ? (
            <a
              href={`/users/${setter.slug}`}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {body}
            </a>
          ) : (
            <span className="flex items-center gap-1.5 text-muted-foreground">{body}</span>
          );
        })()
      ) : (
        <span />
      )}

      {infoRowTrailing !== undefined ? (
        infoRowTrailing
      ) : (
        <span className="text-xs text-muted-foreground">
          {format(new Date(climb.created_at), 'd MMM yyyy', { locale: dateLocale })}
        </span>
      )}
    </div>

    {adminActions && (
      <div className="flex items-center gap-3 px-3 py-2 border-b">
        {adminActions}
      </div>
    )}
    </>
  );
}

/**
 * Bounding a picture that is drawn **whole** rather than cropped: the marks on
 * it sit at percentages of its box, so letterboxing it would leave them behind.
 * Its height is therefore bounded by bounding its *width* to that height times
 * its aspect — a max-width, so the picture still spans the frame wherever the
 * cap is generous enough to let it, and only narrows (centred) once the height
 * is what binds. Until the photo has loaded there is no aspect to use and a
 * plain max-height stands in: briefly cropped beats a page that lurches when it
 * arrives.
 */
function fitHeight(height: string, aspect: number | null): CSSProperties {
  return {
    maxHeight: height,
    maxWidth: aspect ? `calc(${height} * ${aspect})` : undefined,
  };
}

/** One of the buttons that move the photo closer, in the language they all share. */
function ZoomControl({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      // The picture underneath opens the enlarged view when it is pressed, and a
      // press meant for a button is not a press on the picture.
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="flex items-center justify-center rounded-full bg-black/50 p-1.5 text-white transition-colors hover:bg-black/70 disabled:opacity-40 disabled:hover:bg-black/50"
    >
      {children}
    </button>
  );
}
